import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Wrench, Sparkles, Plus, Library, Pencil, Trash2, Loader2, CheckSquare, X } from "lucide-react";

const levelBadge = {
  ALTA: "bg-destructive/10 text-destructive", ALTO: "bg-destructive/10 text-destructive",
  MEDIA: "bg-[#F59E0B]/10 text-[#B45309]", MEDIO: "bg-[#F59E0B]/10 text-[#B45309]",
  BASSA: "bg-[#00A859]/10 text-[#00784A]", BASSO: "bg-[#00A859]/10 text-[#00784A]",
};
const STATUS_META = {
  todo: { label: "Da fare" }, doing: { label: "In corso" }, done: { label: "Completato" },
};
const emptyForm = { title: "", problem: "", recommendation: "", priority: "MEDIA", effort: "MEDIO", owner: "", due_date: "", status: "todo", recommended_tools: [] };

export function RemediationPanel({ process, canEdit, onChange }) {
  const pid = process.id;
  const [rems, setRems] = useState(process.remediations || []);
  const [library, setLibrary] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/processes/${pid}/remediations`);
    setRems(data);
    onChange?.();
  };
  useEffect(() => { setRems(process.remediations || []); }, [process.remediations]);
  useEffect(() => { api.get("/remediation-library").then(({ data }) => setLibrary(data)).catch(() => {}); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/processes/${pid}/remediations/generate`);
      toast.success(`${data.created} remediation generate`);
      setRems(data.remediations); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fromLibrary = async (libId) => {
    try { await api.post(`/processes/${pid}/remediations/from-library/${libId}`); toast.success("Remediation aggiunta"); setLibOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setForm(emptyForm); setEditId(null); setDialog(true); };
  const openEdit = (r) => {
    setForm({ title: r.title, problem: r.problem || "", recommendation: r.recommendation || "", priority: r.priority, effort: r.effort, owner: r.owner || "", due_date: r.due_date || "", status: r.status, recommended_tools: r.recommended_tools || [] });
    setEditId(r.id); setDialog(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    try {
      if (editId) await api.put(`/processes/${pid}/remediations/${editId}`, form);
      else await api.post(`/processes/${pid}/remediations`, form);
      toast.success("Remediation salvata"); setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (r, status) => {
    try { await api.put(`/processes/${pid}/remediations/${r.id}`, { ...r, status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (rid) => {
    if (!window.confirm("Eliminare questa remediation?")) return;
    await api.delete(`/processes/${pid}/remediations/${rid}`); toast.success("Eliminata"); load();
  };

  const toTask = async (rid) => {
    try { const { data } = await api.post(`/processes/${pid}/remediations/${rid}/to-task`); toast.success("Aggiunta ai task del cliente"); load(); return data; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sorted = [...rems].sort((a, b) => ["ALTA", "MEDIA", "BASSA"].indexOf(a.priority) - ["ALTA", "MEDIA", "BASSA"].indexOf(b.priority));

  return (
    <div className="bg-white border border-border p-6" data-testid="remediation-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-bold text-lg tracking-tight">Remediation</h3>
          <span className="text-xs text-muted-foreground font-mono">({rems.length})</span>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-none" onClick={generate} disabled={busy} data-testid="generate-remediation-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Genera AI
            </Button>
            <Button size="sm" variant="outline" className="rounded-none" onClick={() => setLibOpen(true)} data-testid="from-library-btn"><Library className="w-3.5 h-3.5 mr-1" /> Da libreria</Button>
            <Button size="sm" className="rounded-none" onClick={openNew} data-testid="add-remediation-btn"><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
          </div>
        )}
      </div>

      {rems.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuna remediation. {canEdit ? "Genera con l'AI, importa dalla libreria o aggiungi manualmente azioni con priorità, effort, owner e scadenza." : "Nessuna azione definita."}</p>
      ) : (
        <div className="space-y-3">
          {sorted.map((r, i) => (
            <div key={r.id} data-testid={`remediation-row-${i}`} className={`border border-border p-4 ${r.status === "done" ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 text-[10px] font-bold ${levelBadge[r.priority] || "bg-secondary"}`}>Priorità {r.priority}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold ${levelBadge[r.effort] || "bg-secondary"}`}>Effort {r.effort}</span>
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-secondary text-secondary-foreground uppercase">{r.source}</span>
                    {r.task_id && <span className="px-2 py-0.5 text-[10px] font-bold bg-primary text-primary-foreground">→ TASK</span>}
                  </div>
                  <div className={`font-medium text-sm mt-2 ${r.status === "done" ? "line-through" : ""}`}>{r.title}</div>
                  {r.recommendation && <p className="text-sm text-muted-foreground mt-1">{r.recommendation}</p>}
                  {r.recommended_tools?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {r.recommended_tools.map((t, j) => <span key={j} className="text-[10px] font-mono bg-secondary px-2 py-0.5" title={t.note}>{t.name}</span>)}
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground font-mono">
                    {r.owner && <span>👤 {r.owner}</span>}
                    {r.due_date && <span>📅 {new Date(r.due_date).toLocaleDateString("it-IT")}</span>}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex flex-col items-end gap-2">
                    <Select value={r.status} onValueChange={(v) => changeStatus(r, v)}>
                      <SelectTrigger data-testid={`remediation-status-${i}`} className="w-[120px] h-7 rounded-none text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      {process.client_id && !r.task_id && (
                        <button onClick={() => toTask(r.id)} className="text-muted-foreground hover:text-primary" title="Aggiungi ai task del cliente" data-testid={`remediation-to-task-${i}`}><CheckSquare className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manual/edit dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica remediation" : "Nuova remediation"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="data-label">Titolo *</Label><Input data-testid="remediation-title-input" className="mt-1 rounded-none" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="data-label">Raccomandazione</Label><Textarea className="mt-1 rounded-none" rows={2} value={form.recommendation} onChange={(e) => setForm({ ...form, recommendation: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="data-label">Priorità</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{["ALTA", "MEDIA", "BASSA"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="data-label">Effort</Label>
                <Select value={form.effort} onValueChange={(v) => setForm({ ...form, effort: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{["BASSO", "MEDIO", "ALTO"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="data-label">Owner</Label><Input className="mt-1 rounded-none" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="es. IT Manager" /></div>
              <div><Label className="data-label">Scadenza</Label><Input type="date" className="mt-1 rounded-none font-mono" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialog(false)}>Annulla</Button>
            <Button className="rounded-none" onClick={save} data-testid="save-remediation-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Library picker */}
      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="library-picker">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Libreria Remediation</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {library.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna voce in libreria.</p> : library.map((l, i) => (
              <div key={l.id} className="border border-border p-3 flex items-start justify-between gap-3" data-testid={`library-pick-${i}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold ${levelBadge[l.priority] || "bg-secondary"}`}>{l.priority}</span>
                    {l.category && <span className="text-[10px] font-mono bg-secondary px-2 py-0.5">{l.category}</span>}
                  </div>
                  <div className="font-medium text-sm mt-1">{l.title}</div>
                  <p className="text-xs text-muted-foreground mt-1">{l.recommendation}</p>
                  {l.recommended_tools?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{l.recommended_tools.map((t, j) => <span key={j} className="text-[10px] font-mono bg-secondary px-2 py-0.5">{t.name}</span>)}</div>}
                </div>
                <Button size="sm" className="rounded-none shrink-0" onClick={() => fromLibrary(l.id)}><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
